import { Stepper } from "@mantine/core"
import { setTags as sentrySetTags } from "@sentry/nextjs"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { StepsForm, type TFormStep, useStepsForm } from "@/components/forms"
import { AppLoader } from "@/components/utils"
import { useActivity, useAuth } from "@/hooks"
import { checkSurvey } from "@/types"
import { useSaveSession } from "./hooks"
import { scheduleSteps } from "./steps"
import type { TScheduleProps } from "./types"

export function ScheduleActivity({ activityId, activityType, session }: TScheduleProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { data: activity, isLoading } = useActivity(activityId)

  const steps = (scheduleSteps[activityType ?? 0] || scheduleSteps.other) as TFormStep[]
  const { form, step, setStep } = useStepsForm(steps, { activity, session })
  const saveSession = useSaveSession({ activityId, session })

  useEffect(() => {
    sentrySetTags({
      activity_id: activityId,
      session_id: session?.id || "",
      action: "schedule_activity",
    })
  }, [activityId, session?.id])

  if (isLoading || !activity) {
    return <AppLoader />
  }

  return (
    <StepsForm
      size="xs"
      form={form}
      active={step}
      color="green.8"
      onStepClick={setStep}
      finishLabel="Schedule"
      onSubmit={form.onSubmit(saveSession) as () => void}
      onTest={(_, setStep) => {
        form.setFieldValue("dtstart", Date.now())
        form.setFieldValue("time", new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }))
        form.setFieldValue("visibility", "public")

        if (checkSurvey(activity)) {
          form.setFieldValue("population", "manual")
          form.setFieldValue("meta.user_ids", [user!.id])
        } else {
          form.setFieldValue("population", "manual")
          form.setFieldValue("meta.team_ids", [user!.team_ids![0]])
        }
        setStep?.(steps.length - 1)
      }}
    >
      {steps.map((Step) => (
        <Stepper.Step key={Step.label} label={t(Step.label)}>
          <Step activity={activity} form={form} />
        </Stepper.Step>
      ))}
    </StepsForm>
  )
}
